import React from 'react';
import _ from 'lodash';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { IntlService } from '@progress/kendo-react-intl';
import { fieldFormat } from '../../utils/helper';
import PropTypes from 'prop-types';

/**
 * Edit numeric cell component
 * @module
 * @param {func} onChange - Function invoked on change
 * @param {string} dataItem - Data item
 * @param {string} field - Field
 * @param {string} className - TD Class name
 * @param {number} colSpan - Number of columns which cell expands to
 * @param {object} style - Style
 * @param {number} columnIndex - Column index
 * @param {number} level - Level
 * @param {func} render - Render function
 */

export function EditNumericCell({ getColumns }) {
  class EditNumericCell extends React.Component {
    timeout = null;

    constructor(props) {
      super(props);

      const { field, dataItem } = props;
      const dataValue = _.get(dataItem, field.split('.'));

      this.state = {
        value: dataValue,
      }

      this.inputRef = React.createRef();
    }

    handleKeyDown = (e) => {
      var k = e.keyCode;
      if (e.ctrlKey || k === 8 || k === 9 || k === 13 || k === 27 ||
        (k >= 16 && k <= 20) ||
        (k >= 33 && k <= 40) ||
        (k >= 44 && k <= 46)) {

        if (this.timeout) {
          clearTimeout(this.timeout);
          this.timeout = null;
          this.handleTimeout(new Event('change'));
          e.stopPropagation();
          setTimeout(() => { e.target.dispatchEvent(new KeyboardEvent('keydown', e)); }, 500);
        }
        return;
      }
    }

    handleTimeout = (e) => {
      const { field, dataItem, onChange } = this.props;

      onChange({
        dataItem: dataItem,
        field: field,
        syntheticEvent: e,
        value: this.state.value
      });

      this.timeout = null;
    }

    handleChange = (e) => {
      this.setState({ value: e.value });

      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      this.timeout = setTimeout(() => { this.handleTimeout(e) }, 1000);
    }

    componentDidUpdate(prevProps, prevState) {
      const { field, dataItem, } = this.props;
      const dataValue = _.get(dataItem, field.split('.'));
      const prevDataValue = _.get(prevProps.dataItem, prevProps.field.split('.'));
      if (prevDataValue !== dataValue) {
        this.setState({ value: dataValue });
      }
      if (this.inputRef.current && dataItem.inEdit !== prevProps.dataItem.inEdit) {
        if (dataItem.inEdit) {
          this.inputRef.current.element.addEventListener("keydown", this.handleKeyDown);
        } else {
          this.inputRef.current.element.removeEventListener("keydown", this.handleKeyDown);
        }
      }
    }

    render() {
      const { dataItem, field } = this.props;
      const dataValue = this.state.value
      const column = getColumns().find(item => item.field === field);
      const className = (dataItem.inEdit ? "k-grid-edit-cell " : "") + this.props.className;
      const format = column.format ? fieldFormat(column.format) : "n";
      const formattedValue = new IntlService('sk-SK').format('{0:' + format + '}', dataValue);

      const td = (
        <td colSpan={this.props.colSpan}
          className={className}
          style={this.props.style}
          role="gridcell"
          aria-colindex={(this.props.columnIndex || 0) - (this.props.level || 0) + 1}
          aria-selected={false}
          tabIndex="0"
        >
          {((dataItem.inEdit === true || dataItem.inEdit === this.props.field) && column.editable !== false) ? (
            <NumericTextBox
              onChange={this.handleChange}
              value={dataValue}
              defaultValue={column.defaultValue}
              required={column.required}
              readOnly={column.disabled}
              min={column.min === null ? undefined : column.min}
              max={column.max === null ? undefined : column.max}
              step={column.step === null ? undefined : column.step}
              format={format}
              tabIndex={column.tabIndex}
              spinners={column.spinners}
              onBlur={column.onBlur}
              onFocus={column.onFocus}
              ref={this.inputRef}
            />
          ) : (
            formattedValue
          )}
        </td>
      );
      return this.props.render ? this.props.render.call(undefined, td, this.props) : td;
    }
  }

  EditNumericCell.propTypes = {
    onChange: PropTypes.func,
    dataItem: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    field: PropTypes.string,
    className: PropTypes.string,
    colSpan: PropTypes.number,
    style: PropTypes.object,
    columnIndex: PropTypes.number,
    level: PropTypes.number,
    render: PropTypes.func
  }
  return EditNumericCell
}